import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { MonacoEditorModule } from "ngx-monaco-editor-v2";
import { DiscussionPost, ProblemDetail, ProblemEditorial } from "../../models/problem.models";
import { AuthService } from "../../services/auth.service";
import { SubmissionResult, SubmissionService } from "../../services/submission.service";
import { ProblemService } from "../../services/problem.service";

type ProblemTab = "description" | "editorial" | "submissions" | "discussion";
type ConsoleTab = "output" | "tests" | "result";
type SolveMode = "practice" | "challenge";

const DEFAULT_JAVA_TEMPLATE = `import java.util.*;
import java.util.stream.*;

public class Solution {

    public static Object solve() {
        // write your stream code here
        return null;
    }
}`;

@Component({
  selector: "app-code-editor",
  standalone: true,
  imports: [CommonModule, MonacoEditorModule, FormsModule],
  templateUrl: "./code-editor.component.html",
  styleUrl: "./code-editor.component.css"
})
export class CodeEditorComponent implements OnInit, OnDestroy {
  problem: ProblemDetail | null = null;
  loading = true;
  error = "";

  code = "";
  customInput = "";
  activeMode: SolveMode = "practice";

  runBusy = false;
  submitBusy = false;
  latestResult: SubmissionResult | null = null;

  showHint = false;
  hintText = "";
  hintStep = 0;
  hintTotalSteps = 0;
  hintLoading = false;

  editorialLoading = false;
  editorialError = "";
  editorial: ProblemEditorial | null = null;

  discussionLoading = false;
  discussionError = "";
  discussionPosts: DiscussionPost[] = [];
  discussionDraft = "";
  postingDiscussion = false;

  activeProblemTab: ProblemTab = "description";
  activeConsoleTab: ConsoleTab = "output";
  fullscreenEditor = false;
  loadingSubmissions = false;
  recentSubmissions: Array<{
    id: number;
    problemId: number;
    problemTitle: string;
    status: string;
    executionTime: number | null;
    createdAt: string;
  }> = [];

  private readonly destroy$ = new Subject<void>();

  readonly editorOptions = {
    theme: "vs-dark",
    language: "java",
    automaticLayout: true,
    minimap: { enabled: false },
    fontSize: 14,
    roundedSelection: true,
    scrollBeyondLastLine: false,
    fontFamily: "JetBrains Mono"
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly problemService: ProblemService,
    private readonly submissionService: SubmissionService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get("id"));
      this.loadProblem(id);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectProblemTab(tab: ProblemTab) {
    this.activeProblemTab = tab;
    if (tab === "editorial" && !this.editorial && !this.editorialLoading) {
      this.loadEditorial();
    }
    if (tab === "discussion" && this.discussionPosts.length === 0 && !this.discussionLoading) {
      this.loadDiscussion();
    }
    if (tab === "submissions") {
      this.loadSubmissionHistory(this.problem?.id || 0);
    }
  }

  selectConsoleTab(tab: ConsoleTab) {
    this.activeConsoleTab = tab;
  }

  setMode(mode: SolveMode) {
    this.activeMode = mode;
  }

  toggleFullscreenEditor() {
    this.fullscreenEditor = !this.fullscreenEditor;
  }

  runCode() {
    if (!this.problem || this.runBusy) {
      return;
    }
    this.runBusy = true;
    this.activeConsoleTab = "result";
    this.latestResult = null;

    this.submissionService
      .run(this.problem.id, this.code, {
        mode: this.activeMode,
        customInput: this.customInput
      })
      .subscribe({
        next: (result) => {
          this.latestResult = result;
          this.runBusy = false;
        },
        error: (error) => {
          this.latestResult = {
            submissionId: -1,
            status: "SYSTEM_ERROR",
            output: "",
            failedTests: [error?.error?.message || "Run failed"],
            executionTime: null
          };
          this.runBusy = false;
        }
      });
  }

  submitCode() {
    if (!this.problem || this.submitBusy) {
      return;
    }
    this.submitBusy = true;
    this.activeConsoleTab = "result";

    this.submissionService
      .submit(this.problem.id, this.code, {
        mode: this.activeMode,
        contestId: null
      })
      .subscribe({
        next: (result) => {
          this.latestResult = result;
          this.authService.applySubmissionRewards(result);
          this.submitBusy = false;
          this.loadSubmissionHistory(this.problem?.id || 0);
          if (result.status === "ACCEPTED" && this.activeProblemTab === "editorial") {
            this.loadEditorial();
          }
        },
        error: (error) => {
          this.latestResult = {
            submissionId: -1,
            status: "SYSTEM_ERROR",
            output: "",
            failedTests: [error?.error?.message || "Submit failed"],
            executionTime: null
          };
          this.submitBusy = false;
        }
      });
  }

  revealHint() {
    if (!this.problem || this.hintLoading) {
      return;
    }
    const nextStep = this.hintStep + 1;
    this.hintLoading = true;
    this.problemService.getHintStep(this.problem.id, nextStep).subscribe({
      next: (response) => {
        this.hintText = response.hint;
        this.hintStep = response.step;
        this.hintTotalSteps = response.totalSteps;
        this.showHint = true;
        this.hintLoading = false;
      },
      error: (error) => {
        this.hintText = error?.error?.message || "Hint is unavailable right now";
        this.showHint = true;
        this.hintLoading = false;
      }
    });
  }

  resetTemplate() {
    this.code = this.getInitialCode(this.problem);
    this.latestResult = null;
  }

  openUnlockedLevel(levelId: number) {
    if (!levelId || Number.isNaN(levelId)) {
      return;
    }
    this.router.navigate(["/challenge", levelId]);
  }

  submitDiscussion() {
    if (!this.problem || this.postingDiscussion) {
      return;
    }
    const content = this.discussionDraft.trim();
    if (content.length < 5) {
      this.discussionError = "Discussion must be at least 5 characters.";
      return;
    }
    this.postingDiscussion = true;
    this.discussionError = "";
    this.problemService.postDiscussion(this.problem.id, content).subscribe({
      next: (post) => {
        this.discussionPosts = [post, ...this.discussionPosts];
        this.discussionDraft = "";
        this.postingDiscussion = false;
      },
      error: (error) => {
        this.discussionError = error?.error?.message || "Could not publish discussion post";
        this.postingDiscussion = false;
      }
    });
  }

  toggleUpvote(post: DiscussionPost) {
    this.problemService.upvoteDiscussion(post.id).subscribe({
      next: (result) => {
        this.discussionPosts = this.discussionPosts.map((item) =>
          item.id === post.id ? { ...item, upvotes: result.upvotes, hasUpvoted: result.hasUpvoted } : item
        );
      }
    });
  }

  statusClass(status: string) {
    if (status === "ACCEPTED" || status === "RUN_OK") {
      return "status-accepted";
    }
    return "status-error";
  }

  private getInitialCode(problem: ProblemDetail | null) {
    if (!problem?.starterCode || problem.starterCode.trim().length === 0) {
      return DEFAULT_JAVA_TEMPLATE;
    }
    return problem.starterCode;
  }

  private loadProblem(id: number) {
    if (!id || Number.isNaN(id)) {
      this.problem = null;
      this.error = "Invalid challenge id";
      this.loading = false;
      return;
    }

    this.loading = true;
    this.error = "";
    this.problem = null;
    this.latestResult = null;
    this.showHint = false;
    this.hintText = "";
    this.hintStep = 0;
    this.hintTotalSteps = 0;
    this.editorial = null;
    this.editorialError = "";
    this.discussionPosts = [];
    this.discussionError = "";
    this.discussionDraft = "";
    this.runBusy = false;
    this.submitBusy = false;
    this.activeProblemTab = "description";
    this.activeConsoleTab = "output";

    this.problemService.getProblem(id).subscribe({
      next: (problem) => {
        this.problem = problem;
        this.code = this.getInitialCode(problem);
        this.loading = false;
        this.loadSubmissionHistory(problem.id);
      },
      error: (error) => {
        this.error = error?.error?.message || "Could not load challenge";
        this.loading = false;
      }
    });
  }

  private loadSubmissionHistory(problemId: number) {
    if (!problemId) {
      this.recentSubmissions = [];
      return;
    }
    this.loadingSubmissions = true;
    this.submissionService.getRecent().subscribe({
      next: (rows) => {
        this.recentSubmissions = rows.filter((row) => row.problemId === problemId);
        this.loadingSubmissions = false;
      },
      error: () => {
        this.recentSubmissions = [];
        this.loadingSubmissions = false;
      }
    });
  }

  private loadEditorial() {
    if (!this.problem) {
      return;
    }
    this.editorialLoading = true;
    this.editorialError = "";
    this.problemService.getEditorial(this.problem.id).subscribe({
      next: (editorial) => {
        this.editorial = editorial;
        this.editorialLoading = false;
      },
      error: (error) => {
        this.editorial = null;
        this.editorialError = error?.error?.message || "Editorial unavailable";
        this.editorialLoading = false;
      }
    });
  }

  private loadDiscussion() {
    if (!this.problem) {
      return;
    }
    this.discussionLoading = true;
    this.discussionError = "";
    this.problemService.getDiscussion(this.problem.id).subscribe({
      next: (posts) => {
        this.discussionPosts = posts;
        this.discussionLoading = false;
      },
      error: (error) => {
        this.discussionPosts = [];
        this.discussionError = error?.error?.message || "Could not load discussion";
        this.discussionLoading = false;
      }
    });
  }
}
